import { useEffect } from "react";
import { Link } from "react-router-dom";
import { QrCode, UserPlus, Palette, RefreshCw, FileText, MessageCircle, BarChart3, ArrowRight, ScanLine, Smartphone, Check } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { DemoPhone } from "@/components/landing/DemoPhone";
import { LegalLinks, useCompany, companyLine } from "@/components/legal/LegalLayout";

const CONTACT = "mailto:info@eviprendi.it?subject=Richiesta%20digital%20card%20SALVILO";
const DEMOS = ["luca-bianchi", "giulia-marchetti", "marco-ferri", "elena-rossi"];

const FEATURES = [
  { Icon: QrCode, title: "QR permanente", text: "Il codice punta sempre alla tua pagina. Cambi numero, foto o link: il QR stampato continua a funzionare." },
  { Icon: UserPlus, title: "Salva contatto", text: "Un tocco e il tuo contatto è pronto per la rubrica di iPhone e Android, sempre aggiornato." },
  { Icon: Palette, title: "Design su misura", text: "Otto temi, colori, font, sfondi e icone personalizzate: ogni card ha la sua identità visiva." },
  { Icon: RefreshCw, title: "Aggiorni quando vuoi", text: "Modifiche visibili subito, senza ristampare. Con l'accesso personale puoi aggiornarla anche da solo." },
  { Icon: FileText, title: "Listini e PDF", text: "Allega listino prezzi, menu o brochure, scaricabili direttamente dalla card." },
  { Icon: MessageCircle, title: "WhatsApp diretto", text: "Chi ti visita ti scrive su WhatsApp con un messaggio già pronto da inviare." },
  { Icon: BarChart3, title: "Statistiche rispettose", text: "Visite, scansioni e click in forma aggregata, senza cookie di tracciamento." },
];

const STEPS = [
  { Icon: ScanLine, n: "01", title: "Scansiona", text: "Il QR sul biglietto, sulla vetrina o sulla brochure apre la tua pagina." },
  { Icon: Smartphone, n: "02", title: "Scopri", text: "Contatti, social, servizi e documenti in una pagina elegante, pensata per lo smartphone." },
  { Icon: UserPlus, n: "03", title: "Salva", text: "Il contatto finisce in rubrica con nome, ruolo, azienda, telefono ed email." },
];

const Nav = () => (
  <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/85 backdrop-blur-xl">
    <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
      <Logo size={26} inverted testId="landing-logo" />
      <nav className="hidden items-center gap-8 text-sm text-zinc-400 md:flex">
        <a href="#come-funziona" className="transition-colors hover:text-white" data-testid="landing-nav-how">Come funziona</a>
        <a href="#funzioni" className="transition-colors hover:text-white" data-testid="landing-nav-features">Funzioni</a>
        <a href="#esempi" className="transition-colors hover:text-white" data-testid="landing-nav-examples">Esempi</a>
      </nav>
      <div className="flex items-center gap-4">
        <Link to="/login" className="text-sm text-zinc-400 transition-colors hover:text-white" data-testid="landing-login-link">Accedi</Link>
        <a href={CONTACT} className="rounded-full bg-white px-4 py-2 text-sm font-medium text-black transition-opacity hover:opacity-85" data-testid="landing-nav-cta">Richiedi la tua card</a>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative overflow-hidden bg-black pb-20 pt-36 text-white sm:pt-44">
    <div className="pointer-events-none absolute left-1/2 top-0 h-[620px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(200,90,50,0.35),transparent_60%)] blur-2xl" />
    <div className="pointer-events-none absolute inset-0 opacity-[0.18] [background-image:linear-gradient(#27272a_1px,transparent_1px),linear-gradient(90deg,#27272a_1px,transparent_1px)] [background-size:48px_48px] [mask-image:linear-gradient(to_bottom,#000,transparent_70%)]" />
    <div className="relative mx-auto max-w-6xl px-5 text-center sm:px-8">
      <div className="fade-up mx-auto inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-zinc-400">
        <span className="dcs-pulse h-1.5 w-1.5 rounded-full bg-[#C85A32]" />Digital business card
      </div>
      <h1 className="fade-up font-display mt-8 text-5xl leading-[0.98] sm:text-7xl lg:text-8xl" data-testid="landing-hero-title">
        Il tuo biglietto da visita.<br /><span className="text-zinc-500">Sempre aggiornato.</span>
      </h1>
      <p className="fade-up mx-auto mt-7 max-w-xl text-base text-zinc-400 sm:text-lg">Una pagina elegante, un QR che non scade mai, un contatto che si salva in rubrica con un tocco.</p>
      <div className="fade-up mt-10 flex flex-wrap justify-center gap-3">
        <a href={CONTACT} className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-medium text-black transition-transform hover:scale-[1.03]" data-testid="landing-hero-cta">Richiedi la tua card <ArrowRight className="h-4 w-4" /></a>
        <Link to="/c/luca-bianchi" className="inline-flex items-center rounded-full border border-white/20 px-6 py-3.5 text-sm text-white transition-colors hover:bg-white/10" data-testid="landing-hero-example">Guarda un esempio</Link>
      </div>
      <div className="relative mt-20 flex items-end justify-center gap-6">
        <DemoPhone slug="giulia-marchetti" scale={0.62} className="hidden -rotate-6 opacity-80 md:block" testId="hero-phone-left" />
        <DemoPhone slug="luca-bianchi" scale={0.82} className="relative z-10" testId="hero-phone-center" />
        <DemoPhone slug="marco-ferri" scale={0.62} className="hidden rotate-6 opacity-80 md:block" testId="hero-phone-right" />
      </div>
    </div>
  </section>
);

const How = () => (
  <section id="come-funziona" className="bg-white py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="eyebrow">Come funziona</div>
      <h2 className="font-display mt-3 max-w-2xl text-4xl sm:text-5xl">Tre gesti. Nessuna app da installare.</h2>
      <div className="mt-16 grid gap-10 md:grid-cols-3">
        {STEPS.map(({ Icon, n, title, text }) => (
          <div key={n} className="border-t border-black/10 pt-6" data-testid={`landing-step-${n}`}>
            <div className="flex items-center justify-between"><span className="font-mono-ui text-xs text-zinc-400">{n}</span><Icon className="h-5 w-5 text-[#C85A32]" strokeWidth={1.5} /></div>
            <h3 className="font-display mt-6 text-2xl">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-500">{text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Features = () => (
  <section id="funzioni" className="bg-[#F5F5F7] py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="eyebrow">Funzioni</div>
      <h2 className="font-display mt-3 max-w-2xl text-4xl sm:text-5xl">Tutto quello che un biglietto di carta non può fare.</h2>
      <div className="mt-16 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURES.map(({ Icon, title, text }, i) => (
          <div key={title} className={`group rounded-3xl p-7 transition-transform duration-300 hover:-translate-y-1 ${i === 0 ? "bg-black text-white lg:col-span-2 lg:row-span-2 lg:flex lg:flex-col lg:justify-between" : "bg-white"}`} data-testid={`landing-feature-${i}`}>
            <Icon className={`h-6 w-6 ${i === 0 ? "text-[#C85A32]" : "text-black"}`} strokeWidth={1.5} />
            <div>
              <h3 className={`font-display mt-8 ${i === 0 ? "text-4xl" : "text-xl"}`}>{title}</h3>
              <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? "max-w-sm text-zinc-400" : "text-zinc-500"}`}>{text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Examples = () => (
  <section id="esempi" className="overflow-hidden bg-white py-28">
    <div className="mx-auto max-w-6xl px-5 sm:px-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <div className="eyebrow">Esempi</div>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl">Ogni card, un'identità.</h2>
        </div>
        <p className="max-w-xs text-sm text-zinc-500">Tocca una card per aprirla. Gli esempi usano dati di fantasia.</p>
      </div>
      <div className="mt-14 flex gap-6 overflow-x-auto pb-6 [scrollbar-width:none]">
        {DEMOS.map((s) => <DemoPhone key={s} slug={s} scale={0.66} testId={`landing-example-${s}`} />)}
      </div>
    </div>
  </section>
);

const Cta = () => (
  <section className="relative overflow-hidden bg-black py-28 text-white">
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-[radial-gradient(ellipse_at_bottom,rgba(200,90,50,0.3),transparent_65%)]" />
    <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
      <h2 className="font-display text-4xl sm:text-6xl">Pronto a farti salvare?</h2>
      <p className="mx-auto mt-5 max-w-md text-zinc-400">Progettiamo la tua card su misura, con la tua grafica, il tuo QR e il tuo indirizzo personale.</p>
      <ul className="mx-auto mt-8 flex max-w-lg flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-zinc-300">
        {["Design personalizzato", "QR per la stampa", "Aggiornamenti illimitati"].map((t) => <li key={t} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#C85A32]" />{t}</li>)}
      </ul>
      <a href={CONTACT} className="mt-10 inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-medium text-black transition-transform hover:scale-[1.03]" data-testid="landing-final-cta">Richiedi la tua card <ArrowRight className="h-4 w-4" /></a>
    </div>
  </section>
);

const Footer = () => {
  const s = useCompany();
  return (
  <footer className="border-t border-white/10 bg-black py-10 text-sm text-zinc-500">
    <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 sm:flex-row sm:px-8">
      <Logo size={22} inverted testId="landing-footer-logo" />
      <div className="flex flex-wrap justify-center gap-6">
        <LegalLinks prefix="landing" className="[&_*:hover]:text-white" />
        <Link to="/login" className="hover:text-white">Area riservata</Link>
      </div>
      <span>© {new Date().getFullYear()} SALVILO</span>
    </div>
    <div className="mx-auto mt-6 max-w-6xl px-5 text-center text-xs text-zinc-400 sm:px-8 sm:text-left" data-testid="landing-company-info">{companyLine(s)}</div>
  </footer>
  );
};

export default function Landing() {
  useEffect(() => {
    document.title = "SALVILO — Il biglietto da visita digitale, sempre aggiornato";
    document.querySelector('meta[name="description"]')?.setAttribute("content", "Digital business card con QR permanente, salvataggio contatto in rubrica e design su misura.");
  }, []);
  return (
    <div className="overflow-x-hidden bg-black" data-testid="landing-page">
      <Nav />
      <Hero />
      <How />
      <Features />
      <Examples />
      <Cta />
      <Footer />
    </div>
  );
}
